// =============================================================================
// features/meu-whatsapp/useExcludedContacts.ts — Lista de exclusão de
// contatos pessoais (F51-S12).
//
// POST   /api/channels/waha/me/excluded-contacts       — marcar
// GET    /api/channels/waha/me/excluded-contacts       — listar os meus
// DELETE /api/channels/waha/me/excluded-contacts/:id   — desmarcar
//
// Nenhum hook aqui recebe `channelId`/`agentId` — mesmo padrão de
// `useMeuWhatsApp.ts`: o backend deriva a caixa da sessão, sempre a do
// vendedor logado (D4, nunca a de outro).
//
// LGPD: o número digitado pelo vendedor só viaja no POST (para ser hasheado
// no backend) — nunca volta na resposta, nunca é armazenado em estado global
// além do próprio input do formulário.
// =============================================================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { useToast } from '../../components/ui/Toast';
import { ApiError, api } from '../../lib/api';

// ---------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------

const ExcludedContactSchema = z.object({
  id: z.string().uuid(),
  contact_name_snapshot: z.string().nullable(),
  created_at: z.string(),
});

const ExcludedContactListSchema = z.object({
  data: z.array(ExcludedContactSchema),
});

export type ExcludedContact = z.infer<typeof ExcludedContactSchema>;

/** Só dígitos, 10 a 15 — mesmo contrato do backend. */
export const CONTACT_REMOTE_ID_RE = /^\d{10,15}$/;

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const EXCLUDED_CONTACTS_KEY = ['meu-whatsapp', 'excluded-contacts'] as const;

// ---------------------------------------------------------------------------
// useExcludedContacts — leitura
// ---------------------------------------------------------------------------

/** Lista os contatos pessoais excluídos da minha caixa. Só busca quando `enabled`. */
export function useExcludedContacts(
  opts: { enabled: boolean } = { enabled: true },
): ReturnType<typeof useQuery<ExcludedContact[]>> {
  return useQuery<ExcludedContact[]>({
    queryKey: EXCLUDED_CONTACTS_KEY,
    queryFn: async () => {
      const raw = await api.get('/api/channels/waha/me/excluded-contacts');
      return ExcludedContactListSchema.parse(raw).data;
    },
    enabled: opts.enabled,
    staleTime: 15_000,
  });
}

// ---------------------------------------------------------------------------
// useMarkContactExcluded — mutation (marcar)
// ---------------------------------------------------------------------------

export function useMarkContactExcluded(): ReturnType<
  typeof useMutation<ExcludedContact, Error, string>
> {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation<ExcludedContact, Error, string>({
    mutationFn: async (contactRemoteId: string) => {
      const raw = await api.post('/api/channels/waha/me/excluded-contacts', {
        contact_remote_id: contactRemoteId,
      });
      return ExcludedContactSchema.parse(raw);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: EXCLUDED_CONTACTS_KEY });
      toast('Contato marcado como pessoal — a conversa saiu da sua caixa.', 'success');
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 404) {
        toast('Você ainda não conectou seu WhatsApp.', 'danger');
        return;
      }
      if (err instanceof ApiError && err.status === 400) {
        toast('Número inválido — use só dígitos, sem "+", espaço ou traço.', 'danger');
        return;
      }
      toast('Não foi possível marcar este contato. Tente novamente.', 'danger');
    },
  });
}

// ---------------------------------------------------------------------------
// useUnmarkContactExcluded — mutation (desmarcar)
// ---------------------------------------------------------------------------

export function useUnmarkContactExcluded(): ReturnType<typeof useMutation<void, Error, string>> {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation<void, Error, string>({
    mutationFn: async (id: string) => {
      await api.delete(`/api/channels/waha/me/excluded-contacts/${encodeURIComponent(id)}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: EXCLUDED_CONTACTS_KEY });
      toast('Contato desmarcado — mensagens novas voltam a aparecer.', 'success');
    },
    onError: () => {
      toast('Não foi possível desmarcar este contato. Tente novamente.', 'danger');
    },
  });
}
